import { apiClient } from "api/APIClient";
import { entityAPI } from "api/GenericAPI";

import {
  clearResolvedBackendOrigin,
  normaliseAccountTypeId,
  redirectToFrontendForAccountType,
  storeResolvedBackendOriginForAccountType,
} from "api/DomainResolver";

import {
  AccountType,
  Organisation,
  Permission,
  Role,
  Profile,
  CreateProfilePayload,
  ProfileDisplay,
  User,
} from "api/Accounts/AccountsInterfaces";

import { setAuthTokens, clearAuthTokens } from "api/APIClient";

// ===================================================
// Standard Account CRUD APIs
// ===================================================

export const accountTypeAPI = entityAPI<AccountType>("accounts", "account_type");

export const organisationAPI = entityAPI<Organisation>(
  "accounts",
  "organisation"
);

export const permissionAPI = entityAPI<Permission>("accounts", "permission");

export const roleAPI = entityAPI<Role>("accounts", "role");

export const profileAPI = entityAPI<Profile>("accounts", "profile");

// ============================================================================
// AUTH API
// ============================================================================

export async function login(username: string, password: string) {
  clearResolvedBackendOrigin();
  clearAuthTokens();

  const response = await apiClient.post("/accounts/login/", {
    username,
    password,
  });

  const { access, refresh } = response.data;

  if (!access || !refresh) {
    throw new Error("Login response did not include access and refresh tokens.");
  }

  /**
   * This is the missing step.
   * Without this, organisationAPI.getByLoggedInUser() gets a 401.
   */
  setAuthTokens({ access, refresh });

  /**
   * Fetch organisation once inside login so we can determine the correct domain.
   */
  const organisation = await organisationAPI.getByLoggedInUser();

  const accountTypeId = normaliseAccountTypeId(organisation.account_type);

  if (accountTypeId === null) {
    return {
      ...response.data,
      redirected: false,
      account_type: null,
      account_type_missing: true,
    };
  }

  storeResolvedBackendOriginForAccountType(accountTypeId);

  const redirected = redirectToFrontendForAccountType(accountTypeId, "/");

  return {
    ...response.data,
    redirected,
    account_type: accountTypeId,
    account_type_missing: false,
  };
}

export async function validateRegister(data: {
  username: string;
  email?: string;
  password: string;
}) {
  return apiClient.post("/accounts/register/?validation=1", data);
}

export async function register(data: {
  username: string;
  email?: string;
  password: string;
}) {
  return apiClient.post("/accounts/register/", data);
}

export async function completeRegister(data: {
  account: {
    username: string;
    email?: string;
    password: string;
  };
  profile: {
    first_name: string;
    last_name: string;
    phone_number?: string;
    department?: string;
    position_title?: string;
    role?: number;
    permissions?: number[];
  };
  organisation: {
    name: string;
    description?: string;
    account_type?: number;
    logo?: File | null;
    email: string;
    phone_number: string;
    website?: string;
    address: string;
    size_on_record?: number | null;
    size_at_service?: number | null;
    denomination?: string | null;
  };
}) {
  const formData = new FormData();

  formData.append("account", JSON.stringify(data.account));
  formData.append("profile", JSON.stringify(data.profile));

  const organisationWithoutLogo = {
    ...data.organisation,
    logo: undefined,
  };

  formData.append("organisation", JSON.stringify(organisationWithoutLogo));

  if (data.organisation.logo) {
    formData.append("logo", data.organisation.logo);
  }

  const response = await apiClient.post(
    "/accounts/complete-register/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
}

export async function createStripeSetupSession(organisation_id: number) {
  const response = await apiClient.post(
    "/accounts/stripe/create-setup-session/",
    {
      organisation_id,
    }
  );

  return response.data;
}

export async function completeStripeSetupSession(session_id: string) {
  const response = await apiClient.post(
    "/accounts/stripe/complete-setup-session/",
    {
      session_id,
    }
  );

  return response.data;
}

export async function registerProfile(data: CreateProfilePayload) {
  return apiClient.post("/accounts/profile-register/", data);
}

export async function logout() {
  try {
    await apiClient.post("/accounts/logout/");
  } catch {
    // Ignore. The session may already be invalid.
  }

  clearResolvedBackendOrigin();
  clearAuthTokens();
}

export const getProfileDisplay = async (): Promise<ProfileDisplay> => {
  const { data } = await apiClient.get<ProfileDisplay>(
    "/accounts/profile-display/"
  );

  return data;
};

export const updateUserField = async (
  field: string,
  value: any
): Promise<User> => {
  const { data } = await apiClient.patch<User>("/accounts/update_user/", {
    [field]: value,
  });

  return data;
};


export async function completeCollaboratorRegister(
  publicToken: string,
  data: {
    account: {
      username: string;
      email?: string;
      password: string;
    };
    profile: {
      first_name: string;
      last_name: string;
      phone_number?: string;
      department?: string;
      position_title?: string;
      role?: number;
      permissions?: number[];
    };
  }
) {
  const formData = new FormData();

  formData.append("account", JSON.stringify(data.account));
  formData.append("profile", JSON.stringify(data.profile));

  const response = await apiClient.post(
    `/accounts/complete-collaborator-register/${encodeURIComponent(
      publicToken
    )}/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
}